import { useCallback, useEffect, useRef, useState } from "react"
import { deleteSavedFlight, listSavedFlights, lookupSavedFlights, saveFlight, snapshotFromFlight } from "@/shared/api/aviabooking"
import type { ISearchFlight, SavedFlightItem } from "@/shared/api/aviabooking"

const validOfferId = (id: string) => /^[A-Za-z0-9._~:/+=-]{1,512}$/.test(id)

export function useSavedFlights() {
    const [items, setItems] = useState<SavedFlightItem[]>([])
    const [total, setTotal] = useState(0)
    const [nextOffset, setNextOffset] = useState(0)
    const [loading, setLoading] = useState(false)
    const [pending, setPending] = useState<string | null>(null)
    const [error, setError] = useState("")
    const [failedOffset, setFailedOffset] = useState<number | null>(null)
    const [actionError, setActionError] = useState("")
    const [lookupError, setLookupError] = useState("")
    // null means a completed lookup found no saved offer; missing means not checked.
    const [matches, setMatches] = useState<Record<string, string | null>>({})
    const [lookupLoading, setLookupLoading] = useState(false)
    const visibleIds = useRef<string[]>([])
    const lookupVersion = useRef(0)
    const listVersion = useRef(0)
    const pendingRef = useRef(false)

    const load = useCallback(async (offset: number) => {
        const version = ++listVersion.current
        setLoading(true)
        setError("")
        try {
            const { data } = await listSavedFlights(offset)
            if (version !== listVersion.current) return
            setItems(previous => offset === 0 ? data.items :
                [...previous, ...data.items.filter(item => !previous.some(existing => existing.id === item.id))])
            setTotal(data.total)
            setNextOffset(data.offset + data.items.length)
            setFailedOffset(null)
        } catch {
            if (version === listVersion.current) {
                setFailedOffset(offset)
                setError("Не удалось загрузить сохранённые рейсы. Попробуйте ещё раз.")
            }
        } finally {
            if (version === listVersion.current) setLoading(false)
        }
    }, [])

    useEffect(() => { void load(0) }, [load])

    const lookup = useCallback(async (ids: string[]) => {
        const unique = Array.from(new Set(ids.filter(validOfferId)))
        visibleIds.current = unique
        const version = ++lookupVersion.current
        setLookupError("")
        if (!unique.length) {
            setLookupLoading(false)
            return
        }
        setLookupLoading(true)
        try {
            const pages = []
            for (let i = 0; i < unique.length; i += 50) {
                const { data } = await lookupSavedFlights(unique.slice(i, i + 50))
                pages.push(...data)
            }
            if (version !== lookupVersion.current) return
            const found = new Map(pages.map(item => [item.offer_id, item.id]))
            setMatches(previous => {
                const updated = { ...previous }
                for (const id of unique) updated[id] = found.get(id) ?? null
                return updated
            })
        } catch {
            // Keep previously resolved hearts intact on a transient lookup failure.
            if (version === lookupVersion.current) setLookupError("Не удалось проверить сохранённые рейсы. Повторите проверку.")
        } finally {
            if (version === lookupVersion.current) setLookupLoading(false)
        }
    }, [])

    const refreshAfterMutation = async () => {
        ++lookupVersion.current
        void lookup(visibleIds.current)
        // Never merge a page obtained before the mutation into the new list.
        ++listVersion.current
        await load(0)
    }

    const toggle = async (flight: ISearchFlight) => {
        if (pendingRef.current || (!(flight.id in matches) && !items.some(item => item.offer_id === flight.id))) return
        setActionError("")
        const existingId = matches[flight.id] ?? items.find(item => item.offer_id === flight.id)?.id
        if (!existingId) {
            const snapshot = snapshotFromFlight(flight)
            if (!snapshot || !validOfferId(flight.id)) {
                setActionError("Этот рейс нельзя сохранить: отсутствуют корректные данные о цене или маршруте.")
                return
            }
            pendingRef.current = true
            setPending(flight.id)
            try {
                const { data } = await saveFlight(flight.id, snapshot)
                setMatches(previous => ({ ...previous, [flight.id]: data.id }))
                // The server may return an existing item beyond the loaded range.
                // Re-read page zero rather than inserting it at the wrong position.
                await refreshAfterMutation()
            } catch {
                setActionError("Не удалось сохранить рейс. Попробуйте ещё раз.")
            } finally {
                pendingRef.current = false
                setPending(null)
            }
        } else {
            await removeById(flight.id, existingId)
        }
    }

    const removeById = async (offerId: string, id: string) => {
        if (pendingRef.current) return
        pendingRef.current = true
        setPending(offerId)
        setActionError("")
        try {
            await deleteSavedFlight(id)
            setMatches(previous => ({ ...previous, [offerId]: null }))
            setItems(previous => previous.filter(saved => saved.id !== id))
            setTotal(previous => Math.max(0, previous - 1))
            await refreshAfterMutation()
        } catch {
            setActionError("Не удалось удалить сохранённый рейс. Попробуйте ещё раз.")
        } finally {
            pendingRef.current = false
            setPending(null)
        }
    }

    const remove = (item: SavedFlightItem) => removeById(item.offer_id, item.id)
    const isSaved = (offerId: string) => items.some(item => item.offer_id === offerId) || (
        matches[offerId] !== undefined && matches[offerId] !== null
    )

    return { items, total, nextOffset, loading, pending, error, failedOffset, actionError, lookupError, lookupLoading,
        matches, isSaved, lookup, load, toggle, remove }
}
